import { IconButton, Select, Text, useLoading, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import {
  CoeOfferDetailEntity,
  ErrorListResponse,
  instanceOfErrorListResponse,
} from '@genial/services';
import { useNavigation } from '@react-navigation/native';
import { Box, Skeleton } from 'native-base';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useBatchTicketActions } from '../../../../../../../../store/batchTicket/actions';
import { basketCustomerOrderResumeState } from '../../../../../../../../store/batchTicket/atom';
import { CoeOfferDetailsQuery } from '../../../../../../../../store/batchTicket/selectors';
import { AssetSelectedTypeMap } from '../../../../../../../../store/product/types';
import {
  errorMessageBatchTicketRequestItem,
  successAddBillingTypeIdMessage,
  successReintegratedMessage,
} from '../../../../../../../BatchTicketRequests/utils';
import { putCustomerOfferByBasketService } from '../../../../../../services';
import { PutCustomerOfferByBasketServiceProps } from '../../../../../../utils/types';

interface BillingTypeSelectProps {
  data: AssetSelectedTypeMap['coes'];
}

const Container = ({ children }: { children: React.ReactNode }) => (
  <Box w="full" maxW={{ sm: '180px' }}>
    {children}
  </Box>
);

const normalizeOptions = (data?: CoeOfferDetailEntity) => {
  const defaultLabel = 'Default';

  return (
    data?.allowedBillingType?.map(({ billingDescription, billingTypeId }) => ({
      label: billingDescription?.trim() || defaultLabel,
      value: billingTypeId?.toString(),
    })) || []
  );
};

export const BillingTypeSelect: React.FC<BillingTypeSelectProps> = ({ data }) => {
  const {
    data: response,
    loading: isLoading,
    error: errorQuery,
    refresher,
  } = useRecoilQuery(CoeOfferDetailsQuery({ productId: data?.productId! }));

  const basketCustomerOrderResume = useRecoilValue(basketCustomerOrderResumeState);

  const { goBack } = useNavigation();
  const { updateBasketCustomerCoeOrderItem } = useBatchTicketActions();
  const { showToast, showToastFromError } = useToastCreator('toast-select-billing-type');
  const { hide, show } = useLoading();

  const [billingTypeSelected, setBillingTypeSelected] = useState<null | string>(null);

  const options = normalizeOptions(response?.data);
  const optionsIsEmpty = options.length === 0;

  const placeholder =
    errorQuery || optionsIsEmpty ? 'Houve um erro ao carregar informações' : 'Selecione';

  const handleUpdateBillingTypeId = async (value: string) => {
    try {
      show({
        title: 'Aguarde estamos adicionando o tipo de boletagem a oferta deste cliente.',
        description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
      });

      const updatedCustomerOfferData = {
        ...(data as unknown as PutCustomerOfferByBasketServiceProps),
        billingTypeId: Number(value),
      };

      const updatedCustomerOfferByBasketData = await putCustomerOfferByBasketService(
        data?.basketCustomerOfferId!,
        updatedCustomerOfferData
      );

      updateBasketCustomerCoeOrderItem(updatedCustomerOfferByBasketData);
      setBillingTypeSelected(value);

      const currentBasketCustomerOrderItem = basketCustomerOrderResume.data.all.find(
        (basketCustomerOrderItem) =>
          basketCustomerOrderItem.basketCustomerOrderId === data.basketCustomerOrderId
      );

      const customerBasketItemErrosCount =
        currentBasketCustomerOrderItem?.customerOffers?.reduce((acc, curr) => {
          const basketOffersErrosCount = curr.basketOffers?.reduce(
            (basketOfferAcc, basketOfferCurr) => {
              return basketOfferAcc + (basketOfferCurr.errors?.length || 0);
            },
            0
          );

          return acc + (basketOffersErrosCount || 0);
        }, 0) || 0;

      const customerBasketItemIsLastError = customerBasketItemErrosCount <= 1;

      const toastMessage = customerBasketItemIsLastError
        ? successReintegratedMessage
        : successAddBillingTypeIdMessage;

      showToast(...toastMessage);

      if (customerBasketItemIsLastError) {
        goBack();
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  if (isLoading) {
    return (
      <Container>
        <VStack space={Spacing['inline.quarck']}>
          <Skeleton
            w="full"
            h={Spacing['inset.sm']}
            borderRadius={BoxRadius.sm}
            endColor={Color['brand.primary.lightest']}
          />
          <Skeleton
            w="full"
            h={Spacing['inset.lg']}
            borderRadius={BoxRadius.sm}
            endColor={Color['brand.primary.lightest']}
          />
        </VStack>
      </Container>
    );
  }

  return (
    <Container>
      <HStack alignItems={'center'} space={Spacing['stack.quarck']}>
        <Text fontSize={Typography['fontsize.md']}>Tipo de boletagem</Text>
        <IconButton
          iconSize="xxxs"
          p={0}
          iconColor={Color['brand.tertiary.principal']}
          onPress={refresher}
          testId="button-icon-refresh"
          icon="Refresh"
        />
      </HStack>
      <Select
        testID="select-billing-option"
        options={options}
        triggerProps={{
          isDisabled: optionsIsEmpty,
          borderColor: Color['brand.tertiary.principal'],
          borderWidth: 1,
        }}
        triggerTextProps={{ color: 'brand.tertiary.principal' }}
        onValueChange={handleUpdateBillingTypeId}
        selectedValue={billingTypeSelected || placeholder}
        placeholder={placeholder}
        {...(!billingTypeSelected && {
          helperText: 'Selecione uma opção',
        })}
      />
    </Container>
  );
};
